import { useLanguage } from '@/lib/i18n';
import { motion } from 'framer-motion';
import { Users, Building2, ArrowRight } from 'lucide-react';

const TwoPathwaySection = () => {
  const { tr } = useLanguage();

  const scrollTo = (id: string) => {
    const el = document.querySelector(`#${id}`);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 md:py-32 bg-background relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute left-0 top-1/2 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2" />
      <div className="absolute right-0 bottom-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl translate-x-1/2" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            {tr('pathway.title') || 'Wählen Sie Ihren Weg'}
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {tr('pathway.subtitle') || 'Egal ob Pflegekraft oder Arbeitgeber – wir haben die perfekte Lösung für Sie'}
          </p>
        </motion.div>

        {/* Two pathway cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Nurses Pathway */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/20 rounded-2xl p-8 md:p-10 hover:border-primary/40 transition-all duration-300"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center">
                <Users className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
                {tr('pathway.nurses.title') || 'Für Pflegekräfte'}
              </h3>
            </div>
            
            <p className="text-muted-foreground mb-8">
              {tr('pathway.nurses.desc') || 'Finden Sie neue Karrierechancen und professionelle Anerkennung im In- und Ausland'}
            </p>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/30 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-primary font-bold text-sm">✓</span>
                </div>
                <span className="text-foreground">{tr('pathway.nurses.benefit1') || 'Persönliche Unterstützung & Beratung'}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/30 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-primary font-bold text-sm">✓</span>
                </div>
                <span className="text-foreground">{tr('pathway.nurses.benefit2') || 'Faire Arbeitsbedingungen'}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/30 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-primary font-bold text-sm">✓</span>
                </div>
                <span className="text-foreground">{tr('pathway.nurses.benefit3') || 'Anerkennung Ihrer Qualifikationen'}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/30 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-primary font-bold text-sm">✓</span>
                </div>
                <span className="text-foreground">{tr('pathway.nurses.benefit4') || 'Sprachunterstützung & Training'}</span>
              </li>
            </ul>

            <button
              onClick={() => scrollTo('nurses')}
              className="w-full px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 group"
            >
              {tr('pathway.cta.nurses') || 'Mehr erfahren'}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>

          {/* Employers Pathway */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/20 rounded-2xl p-8 md:p-10 hover:border-primary/40 transition-all duration-300"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center">
                <Building2 className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
                {tr('pathway.employers.title') || 'Für Arbeitgeber'}
              </h3>
            </div>
            
            <p className="text-muted-foreground mb-8">
              {tr('pathway.employers.desc') || 'Finden Sie qualifizierte Fachkräfte und schließen Sie Ihre Personalengpässe schnell und sicher'}
            </p>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/30 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-primary font-bold text-sm">✓</span>
                </div>
                <span className="text-foreground">{tr('pathway.employers.benefit1') || 'Vorqualifizierte Fachkräfte'}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/30 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-primary font-bold text-sm">✓</span>
                </div>
                <span className="text-foreground">{tr('pathway.employers.benefit2') || 'Rechtliche Sicherheit & Compliance'}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/30 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-primary font-bold text-sm">✓</span>
                </div>
                <span className="text-foreground">{tr('pathway.employers.benefit3') || 'Schnelle & effiziente Vermittlung'}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/30 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-primary font-bold text-sm">✓</span>
                </div>
                <span className="text-foreground">{tr('pathway.employers.benefit4') || 'Laufende Unterstützung & Beratung'}</span>
              </li>
            </ul>

            <button
              onClick={() => scrollTo('employers')}
              className="w-full px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 group"
            >
              {tr('pathway.cta.employers') || 'Mehr erfahren'}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TwoPathwaySection;
